import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, ExternalLink, X } from 'lucide-react';
import { FormModal, Field, FilePicker, btnPrimary, btnGhost } from '../components/shared/FormKit';
import NowName from '../components/shared/NowName';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import RefLink from '../components/shared/RefLink';
import WorkflowAddendaList from '../components/records/WorkflowAddendaList';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import { EngagementNameList } from '../components/workflow/CoverageDisplay';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { QcadForm, QcadReviewerFields, qcadReviewerGaps, buildQcadBody, useQcadRecord } from './WorkflowQcadNew';
import {
  natureLabel, impactLabel, eqrLabel, titleFor, PURPOSE_TEXT, CONCLUSION_TEXT, addendumRequiredGaps, addendumRuleProblems,
  parseRiskChanges,
} from './qcAddendum';
import RiskChangesList from '../components/records/RiskChangesList';

// QC6 / QC7 Addendum — detail page (docs/modules/workflow-qcad.md).
// Partner-reviewed under the parent record's policy; approval files the
// addendum on the QC record (and unlocks the PDF).

// History / return-diff labels — must cover the repository's DIFF_FIELDS
// (tests/api/19 checks).
const CHANGE_LABELS = {
  firmName: "Firm's Name",
  nature: 'Nature of Update / Change',
  natureOther: 'Other — Please Specify',
  details: 'Details of Update',
  riskChanged: 'Risk Rating Revised',
  riskFrom: 'Risk Rating — From',
  riskTo: 'Risk Rating — To',
  riskChanges: 'Risk Rating — Per Engagement',
  independenceImpact: 'Independence / Ethical Considerations',
  eqrChanged: 'Engagement Quality Reviewer Reassigned',
  eqrFrom: 'Engagement Quality Reviewer — From',
  eqrTo: 'Engagement Quality Reviewer — To',
  reviewerName: 'Reviewer',
  additionalReviewerName: 'Second Reviewer',
  thirdReviewerName: 'Third Reviewer',
  files: 'Attachments',
};
const fmtValue = (field, v) => {
  if (field === 'eqrFrom' || field === 'eqrTo') return eqrLabel(v);
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'independenceImpact') return impactLabel(v);
  return String(v);
};

const WorkflowQcadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/qcad/${id}`),
        fetchJSON(`/api/workflow/qcad/${id}/events`),
      ]);
      setData(detail);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true); setError(null);
    try {
      const res = await fetchJSON(`/api/workflow/qcad/${id}/${path}`, { method: 'POST', body: JSON.stringify(body || {}) });
      if (path === 'approve') {
        setNotice(res.request?.status === 'approved'
          ? 'Approved — the addendum is now filed on the QC record.'
          : 'Your approval is recorded — the next reviewer has been notified.');
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const request = data?.request;
  const { currentRecord, review, canReview, canModify, canDelete, addendum, engagementLock } = data || {};
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events, canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });
  const title = titleFor(currentRecord?.qcType || request?.recordLabel?.slice(0, 3));

  return (
    <RequestDetailShell backTo="/workflow/qcad" backLabel="QC6 / QC7 Addendum" loading={!data} error={error} notice={notice}>
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel={title}
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            lock={engagementLock}
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={busy}
            events={events}
            onApprove={canReview && request.status === 'requested' ? () => act('approve') : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && request.status === 'awaiting_info' ? () => act('resubmit') : null}
            onDelete={canDelete ? () => setDeleting(true) : null}
          >
            {request.status === 'approved' && (
              <div>
                <p className={detailLabel}>Result</p>
                <p className="text-sm text-green-600 mt-1">Addendum filed on the QC record{addendum?.approvedOn ? ` on ${formatDate(addendum.approvedOn)}` : ''}</p>
                <a href={`${API_BASE_URL}/api/workflow/qcad/${request.id}/pdf`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-sm text-blue-600 hover:text-blue-800">
                  <Download className="w-4 h-4" /> Download {title} (PDF)
                </a>
              </div>
            )}
            {review?.waitingForPriorStage && request.status === 'requested' && (
              <div>
                <p className={detailLabel}>Your Turn</p>
                <p className="text-sm text-slate-600 mt-1">Your stage opens once the reviewer(s) before you have approved.</p>
              </div>
            )}
          </StatusActionsCard>

          <ChangesSinceReturnCard events={events} changeLabels={CHANGE_LABELS} fmtChangeValue={fmtValue} />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title="QC Record">
            <p className="text-sm text-slate-800 break-words">{request.recordLabel}</p>
            {!currentRecord && <p className="text-xs text-amber-600 mt-1">The QC record no longer exists.</p>}
            {currentRecord && (
              <DetailGrid className="mt-3">
                <DetailItem label="QC Form Reference">
                  {currentRecord.qcType}{' '}
                  <RefLink
                    to={`/workflow/qcad/record/${currentRecord.id}`}
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                    title="Open the current QC record in a new tab"
                  >
                    View Current Record <ExternalLink className="w-3.5 h-3.5" />
                  </RefLink>
                </DetailItem>
                <DetailItem label="Date of Original Approval">{currentRecord.approvedOn ? formatDate(currentRecord.approvedOn) : '—'}</DetailItem>
                <DetailItem label={`Engagement(s) Covered (${(currentRecord.engagements || []).length})`}>
                  <EngagementNameList items={currentRecord.engagements || []} keyOf={(e, i) => e.engagementId || i} />
                </DetailItem>
                <DetailItem label="Current Risk Rating">
                  {data.risk?.rating || '—'}{data.risk?.revisedBy ? <span className="text-xs text-slate-500"> (revised by {data.risk.revisedBy}; originally {data.risk.original || '—'})</span> : null}
                </DetailItem>
                <DetailItem label="Current Engagement Quality Reviewer">
                  {eqrLabel(data.eqr?.eqr)}{data.eqr?.revisedBy ? <span className="text-xs text-slate-500"> (set by {data.eqr.revisedBy}; the form named {eqrLabel(data.eqr.original)})</span> : null}
                </DetailItem>
              </DetailGrid>
            )}
          </SectionCard>

          {/* The revisions this one follows — the reviewer reads the addendum
              against them, not against the record alone. The request's own
              addendum (once approved) is the card above, not a row here. */}
          {currentRecord && (
            <SectionCard title={`Other Addenda on This Record (${(data.addenda || []).filter((a) => a.id !== request.addendumId).length})`}>
              <WorkflowAddendaList
                record={currentRecord}
                addenda={data.addenda || []}
                excludeId={request.addendumId || null}
                emptyText="No other addendum has been filed on this record."
              />
            </SectionCard>
          )}

          <SectionCard title={title}>
            <DetailGrid>
              <DetailItem label="Firm's Name" field="firmName">{request.firmName}</DetailItem>
              <DetailItem label="Nature of Update / Change" field="nature">
                {(request.nature || []).map(natureLabel).join(', ') || '—'}
                {(request.nature || []).includes('other') && request.natureOther ? ` — ${request.natureOther}` : ''}
              </DetailItem>
            </DetailGrid>
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-500">Purpose of Addendum</p>
              <p className="text-sm text-slate-600 mt-0.5">{PURPOSE_TEXT}</p>
            </div>
            <div className="mt-3 group">
              <p className="text-xs font-semibold text-slate-500">Details of Update</p>
              <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-line break-words">{request.details || '—'}</p>
              <FieldCommentAnchor field="details" label={CHANGE_LABELS.details} className="mt-0.5" compact hoverOnly />
            </div>
            <DetailGrid className="mt-3">
              <DetailItem label="Risk Rating" field="riskChanged">
                {request.riskChanged && (request.riskChanges || []).length > 0
                  ? <RiskChangesList changes={request.riskChanges} />
                  : request.riskChanged
                  ? <span className="font-medium text-amber-700">Revised from {request.riskFrom || '—'} to {request.riskTo || '—'}</span>
                  : 'Unchanged'}
              </DetailItem>
              {request.riskChanged && (
                <DetailItem label="Engagement Quality Reviewer" field="eqrTo">
                  {request.eqrChanged
                    ? <span className="font-medium text-amber-700">{eqrLabel(request.eqrFrom)} → {eqrLabel(request.eqrTo)}</span>
                    : <>Unchanged — {eqrLabel(request.eqrFrom)}</>}
                </DetailItem>
              )}
              <DetailItem label="Independence / Ethical Considerations" field="independenceImpact">{impactLabel(request.independenceImpact)}</DetailItem>
              <DetailItem label={request.additionalReviewerName ? 'First Reviewer' : 'Reviewer'} field="reviewerName"><NowName value={request.reviewerName} now={request.reviewerNameNow} /></DetailItem>
              {request.additionalReviewerName && (
                <DetailItem label="Second Reviewer" field="additionalReviewerName"><NowName value={request.additionalReviewerName} now={request.additionalReviewerNameNow} /></DetailItem>
              )}
              {request.thirdReviewerName && (
                <DetailItem label="Third Reviewer" field="thirdReviewerName"><NowName value={request.thirdReviewerName} now={request.thirdReviewerNameNow} /></DetailItem>
              )}
            </DetailGrid>
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-500">Conclusion</p>
              <p className="text-sm text-slate-600 mt-0.5">{CONCLUSION_TEXT}</p>
            </div>
          </SectionCard>

          <SectionCard title="Attachments">
            <AttachmentsList files={request.files || []} downloadHref={(f) => `${API_BASE_URL}/api/workflow/qcad/${id}/files/${f.id}`} />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={{ applied: ['Filed on QC record', 'bg-green-50 text-green-700'] }}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtValue}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => { await act('reject', { reason, fieldComments }); setRejecting(false); fc.clear(); }}
            />
          )}

          {modifying && currentRecord && (
            <ModifyModal request={request} events={events} record={currentRecord} onClose={() => setModifying(false)} onDone={() => { setModifying(false); load(); }} />
          )}

          {deleting && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="Withdraw and delete this addendum request? Its uploads are removed too."
              onClose={() => setDeleting(false)}
              onConfirm={async () => { await fetchJSON(`/api/workflow/qcad/${id}`, { method: 'DELETE' }); navigate('/workflow/qcad'); }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify = the same form, prefilled from the request. Save only.
const ModifyModal = ({ request, events = [], record, onClose, onDone }) => {
  const [form, setForm] = useState({
    firmName: request.firmName || '',
    nature: request.nature || [],
    natureOther: request.natureOther || '',
    details: request.details || '',
    riskChanged: request.riskChanged ? 'revised' : 'unchanged',
    riskTo: request.riskTo || '',
    riskChanges: parseRiskChanges(request.riskChanges || []),
    // The reviewer as this request last stated it (changed or kept).
    eqrTo: request.riskChanged ? (request.eqrTo || '') : '',
    independenceImpact: request.independenceImpact || '',
    reviewerStaffId: request.reviewerStaffId ? String(request.reviewerStaffId) : '',
    additionalReviewerStaffId: request.additionalReviewerStaffId ? String(request.additionalReviewerStaffId) : '',
    thirdReviewerStaffId: request.thirdReviewerStaffId ? String(request.thirdReviewerStaffId) : '',
  });
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { recordData } = useQcadRecord(record.id);

  const risk = recordData?.risk || null;
  const eqr = recordData?.eqr || null;
  // Everything still empty, keyed to the QcadForm control it belongs to (the
  // same keys the return-comment anchors use) — after the first Save the modal
  // marks those controls and lists them in the banner.
  const gaps = useMemo(
    () => [...addendumRequiredGaps(form, { needsQmp: false, byEngagement: risk && risk.byEngagement }), ...qcadReviewerGaps(record, form)],
    [record, form, risk]
  );
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    // Rules on filled-in values (the revised rating must move) stay a banner.
    const problems = addendumRuleProblems(form, { riskFrom: risk?.rating || null, byEngagement: risk?.byEngagement });
    if (problems.length > 0) { setError(problems.join('. ')); return; }
    setSaving(true);
    try {
      const fd = buildQcadBody(form, { files, record });
      appendTo(fd);
      await fetchJSON(`/api/workflow/qcad/${request.id}`, { method: 'PATCH', body: fd });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ReturnCommentsBanner events={events} />
        {(error || validation.error) && (
          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <span>{error || validation.error}</span>
            {error && <button type="button" onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}
        <p className="text-sm text-slate-600 break-words">{request.recordLabel}</p>
        <QcadForm
          record={record} risk={risk} eqr={eqr} form={form} setForm={setForm}
          anchor={(k) => k}
        />
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggle}
          downloadHref={(f) => `${API_BASE_URL}/api/workflow/qcad/${request.id}/files/${f.id}`}
          label="Files on This Request"
        />
        <FieldCommentInline field="files" />
        <Field label="Add Supporting Files">
          <FilePicker multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </Field>
        {/* Reviewer picks — last, matching the New page's final Reviewer section. */}
        <QcadReviewerFields record={record} form={form} setForm={setForm} />
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowQcadDetail;
